import { supabase } from '../client';
import { BirdieGameGroup, GameDurationType, RecurringGameGroup, User, toManualUserId } from '@/types';

export interface GameWithPlayers {
  id: string;
  name: string;
  course_id: string;
  start_date: string;
  end_date: string | null;
  duration_type: GameDurationType;
  eligible_tees: string[];
  game_type: string;
  bet: string | null;
  created_at: string;
  updated_at: string;
  user_id: string;
  is_recurring: boolean;
  is_sample: boolean;
  is_lone_wolf: boolean;
  recurring_group_id: string | null;
  players: (User & { image_url?: string | null })[];
}

/**
 * Fetch manual players for a set of game IDs, grouped by game.
 */
const fetchManualPlayersForGames = async (gameIds: string[]): Promise<Map<string, User[]>> => {
  const map = new Map<string, User[]>();
  if (gameIds.length === 0) return map;

  const { data, error } = await supabase
    .from('game_manual_players')
    .select('game_id, manual_players:manual_player_id(id, name, email)')
    .in('game_id', gameIds);

  if (error || !data) return map;

  for (const row of data) {
    const mp = row.manual_players as { id: string; name: string; email: string | null } | null;
    if (!mp) continue;
    const user: User = {
      id: toManualUserId(mp.id),
      name: mp.name,
      email: mp.email,
      isManual: true,
      manualPlayerId: mp.id,
    };
    const existing = map.get(row.game_id) || [];
    existing.push(user);
    map.set(row.game_id, existing);
  }
  return map;
};

export const getGames = async (): Promise<BirdieGameGroup[]> => {
  // Get the current authenticated user
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    console.error('gameQueryService - No authenticated user found');
    return [];
  }

  try {
    // First, find which games this user belongs to
    const { data: userGameLinks, error: linksError } = await supabase
      .from('game_players')
      .select('game_id')
      .eq('player_id', user.id);

    if (linksError) {
      console.error('gameQueryService - Error fetching user game links:', linksError);
      return [];
    }

    if (!userGameLinks || userGameLinks.length === 0) {
      return [];
    }

    const gameIds = userGameLinks.map(link => link.game_id);

    // Then fetch those games with all players
    const { data: games, error } = await supabase
      .from('games')
      .select('*, courses(name, city, state), game_players(profiles:player_id(id, name, email, image_url, is_sample))')
      .in('id', gameIds);

    if (error) {
      console.error('gameQueryService - Error fetching games:', error);
      return [];
    }

    if (!games || games.length === 0) {
      return [];
    }

    // Fetch manual players for all games in one batch
    const manualPlayersMap = await fetchManualPlayersForGames(gameIds);

    // Transform in a single pass
    return games.map(game => {
      const course = game.courses as { name: string; city: string; state: string } | null;
      const courseName = course
        ? `${course.name}, ${course.city}, ${course.state}`
        : undefined;

      const players = game.game_players as Array<{ profiles: { id: string; name: string | null; email: string | null; image_url: string | null; is_sample: boolean } | null }> | null;

      return {
        id: game.id,
        name: game.name,
        courseId: game.course_id,
        courseName,
        year: new Date(game.start_date).getUTCFullYear(),
        startDate: game.start_date,
        endDate: game.end_date,
        durationType: game.duration_type ?? 'open_ended',
        members: [
          ...(players ?? []).map(p => {
            if (p.profiles) {
              return {
                id: p.profiles.id,
                name: p.profiles.name || 'Unknown Player',
                email: p.profiles.email,
                imageUrl: p.profiles.image_url,
                isSample: p.profiles.is_sample ?? false,
              };
            }
            return { id: 'unknown', name: 'Unknown Player' };
          }),
          ...(manualPlayersMap.get(game.id) || []),
        ],
        bet: game.bet || undefined,
        winner: undefined,
        isPaid: false,
        isArchived: game.end_date ? new Date(game.end_date) < new Date() : false,
        isLoneWolf: game.is_lone_wolf ?? false,
        recurringGroupId: game.recurring_group_id ?? null,
        isRecurring: game.is_recurring ?? false,
        isSample: game.is_sample ?? false,
      };
    });
  } catch (error) {
    console.error('gameQueryService - Unexpected error fetching games:', error);
    return [];
  }
};

export const getGamePlayers = async (gameId: string) => {
  // Use explicit table references to avoid ambiguous column references
  const { data, error } = await supabase
    .from('game_players')
    .select('*, profiles:player_id(*)')
    .eq('game_id', gameId);
  
  if (error) {
    console.error('gameQueryService - Error fetching game players:', error);
    return [];
  }
  return data || [];
};

export const getGameWithPlayers = async (gameId: string): Promise<GameWithPlayers | null> => {
  // Get the game with course details
  // RLS will automatically filter access based on the check_game_access function
  const { data: game, error: gameError } = await supabase
    .from('games')
    .select('*, courses(*)')
    .eq('id', gameId)
    .single();
  
  if (gameError) {
    console.error('gameQueryService - Error fetching game:', gameError);
    return null;
  }
  
  // Get players for this game
  const { data: players, error: playersError } = await supabase
    .from('game_players')
    .select('profiles:player_id(*)')
    .eq('game_id', gameId);
  
  if (playersError) {
    console.error('gameQueryService - Error fetching game players:', playersError);
    // Initialize with an empty players array instead of returning the game without players
    return {
      ...game,
      players: []
    };
  }

  // Fetch manual players for this game
  const manualPlayersMap = await fetchManualPlayersForGames([gameId]);
  const manualMembers = manualPlayersMap.get(gameId) || [];

  return {
    ...game,
    players: [
      ...(players?.map(p => p.profiles) || []),
      ...manualMembers,
    ]
  };
};

/**
 * Find all games that a set of players are part of
 */
export const getGamesByPlayers = async (playerIds: string[]): Promise<string[]> => {
  if (!playerIds?.length) return [];
  
  try {
    // Find all games where these players are participants
    const { data, error } = await supabase
      .from('game_players')
      .select('game_id')
      .in('player_id', playerIds);
      
    if (error) {
      console.error('Error finding games by players:', error);
      return [];
    }
    
    // Return unique game IDs
    return [...new Set(data?.map(item => item.game_id) || [])];
  } catch (error) {
    console.error('Unexpected error in getGamesByPlayers:', error);
    return [];
  }
};

/**
 * Get all co-players from a user's active games
 */
export const getCoPlayersFromActiveGames = async (userId: string): Promise<User[]> => {
  // Get all games the user is in
  const { data: userGameLinks } = await supabase
    .from('game_players')
    .select('game_id')
    .eq('player_id', userId);

  if (!userGameLinks || userGameLinks.length === 0) return [];

  const gameIds = userGameLinks.map(link => link.game_id);

  // Get all players in those games (excluding the user)
  const { data: coPlayerLinks } = await supabase
    .from('game_players')
    .select('player_id, profiles:player_id(id, name, email, image_url, is_sample)')
    .in('game_id', gameIds)
    .neq('player_id', userId);

  if (!coPlayerLinks) return [];

  // Deduplicate by player_id
  const seen = new Set<string>();
  const coPlayers: User[] = [];
  for (const link of coPlayerLinks) {
    if (!seen.has(link.player_id)) {
      seen.add(link.player_id);
      const p = link.profiles as { id: string; name: string | null; email: string | null; image_url: string | null; is_sample: boolean };
      coPlayers.push({
        id: p.id,
        name: p.name || 'Unknown',
        email: p.email,
        imageUrl: p.image_url,
        isSample: p.is_sample ?? false,
      });
    }
  }

  // Also fetch manual co-players from those games
  const manualPlayersMap = await fetchManualPlayersForGames(gameIds);
  for (const manualUsers of manualPlayersMap.values()) {
    for (const mu of manualUsers) {
      if (!seen.has(mu.id)) {
        seen.add(mu.id);
        coPlayers.push(mu);
      }
    }
  }

  return coPlayers;
};

/**
 * Pure function: splits a flat games list into recurring groups + standalone games.
 */
export const getRecurringGroups = (games: BirdieGameGroup[]): {
  recurringGroups: RecurringGameGroup[];
  standaloneGames: BirdieGameGroup[];
} => {
  const currentYear = new Date().getUTCFullYear();
  const groupMap = new Map<string, BirdieGameGroup[]>();
  const standaloneGames: BirdieGameGroup[] = [];

  for (const game of games) {
    if (game.recurringGroupId) {
      const existing = groupMap.get(game.recurringGroupId) || [];
      existing.push(game);
      groupMap.set(game.recurringGroupId, existing);
    } else {
      standaloneGames.push(game);
    }
  }

  const recurringGroups: RecurringGameGroup[] = [];
  for (const [groupId, instances] of groupMap) {
    // Sort by year descending
    const sorted = [...instances].sort((a, b) => b.year - a.year);

    // Deduplicate by year — keep first occurrence (most recent by id/insertion order within same year)
    const seenYears = new Set<number>();
    const deduped = sorted.filter(g => {
      if (seenYears.has(g.year)) return false;
      seenYears.add(g.year);
      return true;
    });

    // Derive base name: strip trailing year suffix (e.g. "Warbirds 2026" → "Warbirds")
    const mostRecent = deduped[0];
    const baseName = mostRecent.name.replace(/\s+\d{4}$/, '');

    recurringGroups.push({
      recurringGroupId: groupId,
      name: baseName,
      instances: deduped,
      currentYearInstance: deduped.find(g => g.year === currentYear),
    });
  }

  // Sort recurring groups: Lone Wolf first, then alphabetically
  recurringGroups.sort((a, b) => {
    const aIsLW = a.instances[0]?.isLoneWolf ?? false;
    const bIsLW = b.instances[0]?.isLoneWolf ?? false;
    if (aIsLW && !bIsLW) return -1;
    if (!aIsLW && bIsLW) return 1;
    return a.name.localeCompare(b.name);
  });

  return { recurringGroups, standaloneGames };
};

/**
 * Fetches all game instances in a recurring group from Supabase.
 */
export const getRecurringGroupGames = async (recurringGroupId: string): Promise<BirdieGameGroup[]> => {
  const { data: games, error } = await supabase
    .from('games')
    .select('*, courses(name, city, state), game_players(profiles:player_id(id, name, email, image_url, is_sample))')
    .eq('recurring_group_id', recurringGroupId)
    .order('start_date', { ascending: false });

  if (error || !games) {
    console.error('Error fetching recurring group games:', error);
    return [];
  }

  // Fetch manual players for all games in the group
  const gameIds = games.map(g => g.id);
  const manualPlayersMap = await fetchManualPlayersForGames(gameIds);

  return games.map(game => {
    const course = game.courses as { name: string; city: string; state: string } | null;
    const courseName = course
      ? `${course.name}, ${course.city}, ${course.state}`
      : undefined;

    const players = game.game_players as Array<{ profiles: { id: string; name: string | null; email: string | null; image_url: string | null; is_sample: boolean } | null }> | null;

    return {
      id: game.id,
      name: game.name,
      courseId: game.course_id,
      courseName,
      year: new Date(game.start_date).getUTCFullYear(),
      startDate: game.start_date,
      endDate: game.end_date,
      durationType: game.duration_type ?? 'open_ended',
      members: [
        ...(players ?? []).map(p => {
          if (p.profiles) {
            return {
              id: p.profiles.id,
              name: p.profiles.name || 'Unknown Player',
              email: p.profiles.email,
              imageUrl: p.profiles.image_url,
              isSample: p.profiles.is_sample ?? false,
            };
          }
          return { id: 'unknown', name: 'Unknown Player' };
        }),
        ...(manualPlayersMap.get(game.id) || []),
      ],
      bet: game.bet || undefined,
      isArchived: game.end_date ? new Date(game.end_date) < new Date() : false,
      isLoneWolf: game.is_lone_wolf ?? false,
      recurringGroupId: game.recurring_group_id ?? null,
      isRecurring: game.is_recurring ?? false,
      isSample: game.is_sample ?? false,
    };
  });
};
